import { useContext, useEffect, useState } from 'react';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttTable } from '../../../sporttia/all';
import DeviceDetailsDialog from '../../../pages/sc/devices/DeviceDetailsDialog';
import DateBuilder from '../../../lib/DateBuilder';
import { Booking } from './BookingDialog';
import { DefaultListResponse200 } from '../../../types/api/utils';

type Device = {
    id: number;
    name: string;
};

type DeviceAuth = {
    id: number;
    created: string;
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    weekdays: string;
    devices: Device[];
};

type BookingDialogDeviceAuthsProps = {
    booking: Booking;
};

/**
 * Booking dialog with booking device authentications.
 * @param booking Booking object.
 * @returns {React.ReactElement}
 */
export default function BookingDialogDeviceAuths({
    booking,
}: BookingDialogDeviceAuthsProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [deviceAuths, setDeviceAuths] =
        useState<DefaultListResponse200<DeviceAuth> | null>(null);
    const [deviceId, setDeviceId] = useState<number | null>(null);

    /**
     * Load booking device authentications.
     * @param p Params.
     */
    const loadBookingDeviceAuths = (
        p: { page: number; rows: number } = { page: 1, rows: 20 },
    ) => {
        cxt?.api<DefaultListResponse200<DeviceAuth>>(
            'GET',
            `/bookings/${booking.id}/devices/auths`,
            {
                params: { ...p },
                success: (r: any) => {
                    setDeviceAuths(r);
                },
            },
        );
    };

    useEffect(() => {
        if (booking && booking.id) loadBookingDeviceAuths();
    }, [booking]);

    // -----| Render |-----
    return (
        <>
            <SttTable
                onFetch={loadBookingDeviceAuths}
                data={deviceAuths}
                columns={[
                    {
                        title: t('Created'),
                        field: 'created',
                        value: (row: DeviceAuth) =>
                            new DateBuilder(
                                row?.created,
                                cxt?.sc?.timezone?.name,
                                cxt?.lang?.key,
                            ).dmy(),
                    },
                    {
                        type: 'dmyRange',
                        title: t('Date'),
                        value: (row: DeviceAuth) => ({
                            ini: row.dateini,
                            end: row.dateend,
                        }),
                    },
                    {
                        type: 'hmRange',
                        title: t('Time'),
                        value: (row: DeviceAuth) => ({
                            ini: `${row.dateini} ${row.timeini}`,
                            end: `${row.dateend} ${row.timeend}`,
                        }),
                    },
                    {
                        title: t('Week'),
                        width: 150,
                        type: 'weekdays',
                        field: 'weekdays',
                    },
                    {
                        title: t('Devices'),
                        field: 'devices',
                        value: (row: DeviceAuth) => (
                            <div>
                                {row.devices &&
                                    row.devices.map((device: any) => (
                                        <Chip
                                            key={device.id}
                                            label={device.name}
                                            size="small"
                                            style={{
                                                marginLeft: 4,
                                                marginRight: 4,
                                            }}
                                            onClick={() =>
                                                setDeviceId(device?.id)
                                            }
                                        />
                                    ))}
                            </div>
                        ),
                    },
                ]}
            />
            {deviceId !== null && cxt?.sc && (
                <DeviceDetailsDialog
                    scId={String(cxt.sc?.id)}
                    deviceId={String(deviceId)}
                    onClose={() => {
                        setDeviceId(null);
                    }}
                />
            )}
        </>
    );
}
